<script setup>
import {computed, onMounted, ref, watch} from "vue";
import {useAdminStore} from "@/store/admin";
import {useToast} from "vue-toast-notification";
import MainPreloader from "@/components/shared/MainPreloader.vue";
import EmptyCard from "@/components/shared/EmptyCard.vue";
import AdminModal from "@/components/AdminView/AdminModal.vue";
import ActionButton from "@/components/ui/ActionButton.vue";
import FormInput from "@/components/ui/FormInput.vue";

const adminStore = useAdminStore();
const toast = useToast();
const STATUS_FILTER_STORAGE_KEY = "adminClubSignupsStatusFilter";
const filterStatus = ref(localStorage.getItem(STATUS_FILTER_STORAGE_KEY) ?? 'pending');
watch(filterStatus, value => localStorage.setItem(STATUS_FILTER_STORAGE_KEY, value));
const selected = ref(null);
const decisionComment = ref('');

const signups = computed(() => adminStore.clubSignups);

function formatDate(value) {
  return value ? new Date(value).toLocaleString('ru-RU') : '—';
}

function statusTitle(status) {
  return {
    pending: 'Ожидает',
    approved: 'Одобрена',
    rejected: 'Отклонена',
    rejected_final: 'Отклонена окончательно',
  }[status] || status;
}

async function load() {
  await adminStore.loadClubSignups({status: filterStatus.value});
  if (adminStore.error) toast.error(adminStore.error);
}

function openCard(item) {
  selected.value = item;
  decisionComment.value = '';
}

function closeCard() {
  selected.value = null;
  decisionComment.value = '';
}

async function decide(status) {
  if (!selected.value || selected.value.status !== 'pending') return;
  const captions = {approved: 'одобрить', rejected: 'отклонить', rejected_final: 'отклонить окончательно'};
  if (!confirm(`Подтвердить: ${captions[status] || status} заявку?`)) return;
  const updated = await adminStore.decideClubSignup(selected.value.id, status, decisionComment.value);
  if (!updated) {
    toast.error(adminStore.error || 'Не удалось обработать заявку');
    return;
  }
  const messages = {
    approved: 'Заявка одобрена',
    rejected: 'Заявка отклонена',
    rejected_final: 'Заявка отклонена окончательно',
  };
  toast.success(messages[status] || 'Заявка обработана');
  closeCard();
  await load();
}

onMounted(load);
</script>

<template>
  <div class="adminCrud">
    <MainPreloader v-if="adminStore.loading" position="fixed" :size="80" :stroke="3"/>
    <div class="adminCrud__toolbar">
      <div class="adminCrud__toolbarTitle">Заявки в клуб</div>
      <div class="adminCrud__toolbarActions">
        <select
            v-model="filterStatus"
            class="adminCrud__select adminCrud__select--filter"
            aria-label="Фильтр заявок"
            @change="load"
        >
          <option value="pending">Ожидают решения</option>
          <option value="approved">Одобренные</option>
          <option value="rejected">Отклонённые</option>
          <option value="rejected_final">Отклонённые окончательно</option>
          <option value="">Все</option>
        </select>
      </div>
    </div>
    <EmptyCard v-if="!signups.length && !adminStore.loading"/>
    <table v-else-if="signups.length" class="adminTable">
      <thead>
      <tr>
        <th>Дата</th>
        <th>Заявитель</th>
        <th>Действия</th>
        <th>Контакты</th>
        <th>Статус</th>
      </tr>
      </thead>
      <tbody>
        <tr v-for="item in signups" :key="item.id">
          <td>{{ formatDate(item.submitted_at) }}</td>
          <td>{{ item.last_name }} {{ item.first_name }}<br><small>{{ item.city }}</small></td>
          <td class="adminCrud__actions">
            <button class="adminCrud__actionButton" title="Открыть" @click="openCard(item)">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <circle cx="12" cy="12" r="3" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
          </td>
          <td>{{ item.email }}<br>{{ item.phone }}</td>
          <td>{{ statusTitle(item.status) }}</td>
        </tr>
      </tbody>
    </table>

    <AdminModal :visible="!!selected" title="Заявка в клуб" @close="closeCard">
      <template v-if="selected">
        <div class="adminClubSignups__details">
          <div><b>ФИО:</b> {{ selected.last_name }} {{ selected.first_name }}</div>
          <div><b>Email:</b> {{ selected.email }}</div>
          <div><b>Телефон:</b> {{ selected.phone }}</div>
          <div><b>Город:</b> {{ selected.city }}</div>
          <div><b>Навыки:</b> {{ selected.info }}</div>
          <div><b>Подана:</b> {{ formatDate(selected.submitted_at) }}</div>
          <div v-if="selected.resolved_at"><b>Решение:</b> {{ statusTitle(selected.status) }}, {{ formatDate(selected.resolved_at) }}</div>
          <div v-if="selected.resolved_by_user_name"><b>Обработал:</b> {{ selected.resolved_by_user_name }}</div>
          <div v-if="selected.decision_comment"><b>Комментарий:</b> {{ selected.decision_comment }}</div>
        </div>
        <details class="adminClubSignups__consent"><summary>Устав и условия клуба ({{ selected.club_terms_sha256 }})</summary><div v-html="selected.club_terms_content"></div></details>
        <details class="adminClubSignups__consent"><summary>Согласие на обработку персональных данных ({{ selected.personal_consent_sha256 }})</summary><div v-html="selected.personal_consent_content"></div></details>
        <template v-if="selected.status === 'pending'">
          <label>Комментарий для заявителя</label>
          <FormInput v-model="decisionComment" rows="3"/>
        </template>
      </template>
      <template #footer>
        <ActionButton @buttonClick="closeCard"><template #caption>Закрыть</template></ActionButton>
        <ActionButton v-if="selected?.status === 'pending'" style="background-color: #6FC062; color: #212226;" @buttonClick="decide('approved')"><template #caption>Одобрить</template></ActionButton>
        <ActionButton v-if="selected?.status === 'pending'" @buttonClick="decide('rejected')"><template #caption>Отклонить</template></ActionButton>
        <ActionButton v-if="selected?.status === 'pending'" @buttonClick="decide('rejected_final')"><template #caption>Отклонить окончательно</template></ActionButton>
      </template>
    </AdminModal>
  </div>
</template>

<style scoped lang="scss">
.adminCrud {
  display: flex;
  flex-direction: column;
  gap: 14px;
  text-align: left;

  &__toolbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
  }

  &__toolbarTitle {
    font-size: 20px;
    font-weight: 600;
    color: var(--cd-text);
  }

  &__toolbarActions {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  &__select {
    width: 100%;
    height: 48px;
    border-radius: 20px;
    padding: 5px 20px;
    background: var(--cd-field-bg);
    color: var(--cd-field-color);
    border: 1px solid var(--cd-field-border);
    font-size: 14px;
    font-family: var(--cd-default-font);

    &:focus {
      outline: none;
      border-color: var(--cd-base-green);
      box-shadow: 0 0 5px rgba(98, 192, 100, 0.3);
    }

    &--filter {
      width: auto;
      min-width: 145px;
    }
  }

  &__actions {
    vertical-align: middle;
    white-space: nowrap;
    font-size: 0;

    .adminCrud__actionButton + .adminCrud__actionButton {
      margin-left: 8px;
    }
  }

  &__actionButton {
    background: transparent;
    border: 1px solid var(--cd-base-green);
    color: var(--cd-base-green);
    border-radius: 8px;
    padding: 6px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s;

    &:hover {
      background: var(--cd-base-green);
      color: #ffffff;
    }
  }
}

.adminTable {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0 8px;
  text-align: left;

  @include respond-to('md') {
    display: block;
    overflow-x: auto;
  }

  th {
    color: var(--cd-text-muted);
    font-size: 12px;
    font-weight: 400;
    padding: 0 16px 4px;
  }

  td {
    background: var(--cd-card-bg);
    padding: 12px 16px;
    font-size: 14px;
    color: var(--cd-text);

    &:first-child {
      border-radius: 12px 0 0 12px;
    }

    &:last-child {
      border-radius: 0 12px 12px 0;
    }
  }
}

.adminClubSignups__details { display: grid; gap: 10px; }
.adminClubSignups__consent { padding: 12px; border: 1px solid var(--cd-spoiler-border-color); border-radius: 8px; max-height: 240px; overflow: auto; }
.adminClubSignups__consent summary { cursor: pointer; color: var(--cd-base-green); word-break: break-all; }
small { color: var(--cd-text-muted); }
</style>
